/**
 * Cycle Participation Store
 * 
 * Zustand store for managing item-cycle relationships.
 * Part of Workstream 4: Cycle Boundary Clarity
 * 
 * SUPABASE-ONLY PERSISTENCE:
 * - Primary: Supabase (single source of truth)
 * - No localStorage cache (eliminates stale data issues)
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { v4 as uuidv4 } from 'uuid';
import type {
  CycleItemParticipation,
  InclusionReason,
  CycleItemStatus,
  CycleItemRanking,
  CycleComparisonSet,
  ItemParticipationSummary,
  CycleParticipationSummary,
  AddItemToCycleInput,
  BatchAddItemsToCycleInput,
  SetCycleAttributeValueInput,
  CycleCarryoverOptions,
  CycleCarryoverResult,
} from '../../types/prioritization/cycleParticipation';
import type { AttributeValue } from '../../types/prioritization/lifecycle';
import {
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
  isSupabaseAvailable,
} from '../../lib/supabaseSync';

function getActorId(): string {
  // Resolved at call time to avoid circular import at module level.
  // Falls back to 'system' when called in contexts without an authenticated user.
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { useUserStore } = require('./userStore') as { useUserStore: { getState(): { userId?: string } } };
    return useUserStore.getState().userId ?? 'system';
  } catch {
    return 'system';
  }
}

function emitCycleAuditEvent(
  eventType: string,
  itemId: string,
  cycleId: string,
  note: string,
  details?: Record<string, unknown>,
): void {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { useAuditStore } = require('./auditStore') as { useAuditStore: { getState(): { logEvent(e: unknown): void } } };
    // Pass actorId at top level (used as created_by in DB) and details at top level
    // (auditStore.logEvent picks up `details` only when it is a direct property of eventData,
    // not when nested under payload — see auditStore line: if ('details' in eventData)).
    useAuditStore.getState().logEvent({
      type: eventType,
      itemId,
      note,
      actorId: getActorId(),
      details: { cycleId, ...details },
    });
  } catch {
    // Audit emission is best-effort — do not block participation writes.
  }
}

const PARTICIPATION_TABLE = 'cycle_item_participation';
const COMPARISON_SET_TABLE = 'cycle_comparison_sets';

interface CycleParticipationState {
  // Data - keyed by `${cycleId}_${itemId}`
  participations: Record<string, CycleItemParticipation>;
  
  // Comparison sets by cycle ID
  comparisonSets: Record<string, CycleComparisonSet>;
  
  // Loading state
  isLoading: boolean;
  error: string | null;
  
  // Supabase sync
  fetchFromSupabase: () => Promise<void>;
  
  // Actions
  addItemToCycle: (input: AddItemToCycleInput) => CycleItemParticipation;
  batchAddItemsToCycle: (input: BatchAddItemsToCycleInput) => CycleItemParticipation[];
  removeItemFromCycle: (cycleId: string, itemId: string) => void;
  updateCycleStatus: (cycleId: string, itemId: string, status: CycleItemStatus) => void;
  
  // Attribute value actions
  setCycleAttributeValue: (input: SetCycleAttributeValueInput) => void;
  clearCycleAttributeValue: (cycleId: string, itemId: string, attributeKey: string) => void;
  
  // Ranking actions
  addRanking: (cycleId: string, itemId: string, ranking: Omit<CycleItemRanking, 'rankedAt'>) => void;
  updateItemRanking: (cycleId: string, itemId: string, sessionId: string, rank: number) => void;
  
  // Session participation
  markSessionParticipation: (cycleId: string, itemId: string, sessionId: string) => void;
  
  // Carryover
  carryoverItems: (options: CycleCarryoverOptions) => CycleCarryoverResult;
  
  // Comparison set actions
  createComparisonSet: (cycleId: string, itemIds: string[], criteriaIds: string[]) => CycleComparisonSet;
  updateComparisonSet: (cycleId: string, updates: Partial<CycleComparisonSet>) => void;
  lockComparisonSet: (cycleId: string, lockedBy: string) => void;
  unlockComparisonSet: (cycleId: string) => void;
  
  // Selectors
  getParticipation: (cycleId: string, itemId: string) => CycleItemParticipation | undefined;
  getItemParticipation: (itemId: string, cycleId: string) => CycleItemParticipation | undefined;
  getItemsInCycle: (cycleId: string) => CycleItemParticipation[];
  getCyclesForItem: (itemId: string) => CycleItemParticipation[];
  getCycleItemSummary: (cycleId: string) => { totalItems: number };
  getItemParticipationSummary: (itemId: string) => ItemParticipationSummary;
  getCycleParticipationSummary: (cycleId: string) => CycleParticipationSummary;
  getComparisonSet: (cycleId: string) => CycleComparisonSet | undefined;
  getReadyItems: (cycleId: string, requiredAttributes: string[]) => CycleItemParticipation[];
  getItemsNeedingWork: (cycleId: string, requiredAttributes: string[]) => CycleItemParticipation[];
  
  // Utility
  reset: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

// Helper to create participation key
function participationKey(cycleId: string, itemId: string): string {
  return `${cycleId}_${itemId}`;
}

const initialState = {
  participations: {},
  comparisonSets: {},
  isLoading: false,
  error: null,
};

// Helper to convert DB row to CycleItemParticipation
function dbToParticipation(row: Record<string, unknown>): CycleItemParticipation {
  return {
    id: row.id as string,
    cycleId: row.cycle_id as string,
    itemId: row.item_id as string,
    inclusionReason: row.inclusion_reason as InclusionReason,
    inclusionNote: row.inclusion_note as string | undefined,
    cycleStatus: row.cycle_status as CycleItemStatus,
    cycleAttributeValues: (row.cycle_attribute_values as AttributeValue[]) || [],
    addedAt: new Date(row.added_at as string),
    addedBy: row.added_by as string,
    confirmedAt: row.confirmed_at ? new Date(row.confirmed_at as string) : undefined,
    confirmedBy: row.confirmed_by as string | undefined,
    excludedAt: row.excluded_at ? new Date(row.excluded_at as string) : undefined,
    excludedBy: row.excluded_by as string | undefined,
    excludedReason: row.excluded_reason as string | undefined,
    notes: row.notes as string | undefined,
    participatedInSessionIds: (row.participated_in_session_ids as string[]) || [],
    cycleRankings: (row.cycle_rankings as CycleItemRanking[]) || [],
  };
}

// Helper to convert CycleItemParticipation to DB row
function participationToDb(p: CycleItemParticipation, workspaceId: string | null): Record<string, unknown> {
  return {
    id: p.id,
    cycle_id: p.cycleId,
    item_id: p.itemId,
    inclusion_reason: p.inclusionReason,
    inclusion_note: p.inclusionNote || null,
    cycle_status: p.cycleStatus,
    cycle_attribute_values: p.cycleAttributeValues,
    added_at: p.addedAt.toISOString(),
    added_by: p.addedBy || null,
    confirmed_at: p.confirmedAt?.toISOString() || null,
    confirmed_by: p.confirmedBy || null,
    excluded_at: p.excludedAt?.toISOString() || null,
    excluded_by: p.excludedBy || null,
    excluded_reason: p.excludedReason || null,
    notes: p.notes || null,
    participated_in_session_ids: p.participatedInSessionIds,
    cycle_rankings: p.cycleRankings,
    workspace_id: workspaceId,
  };
}

// Helper to convert DB row to CycleComparisonSet
function dbToComparisonSet(row: Record<string, unknown>): CycleComparisonSet {
  return {
    cycleId: row.cycle_id as string,
    itemIds: (row.item_ids as string[]) || [],
    criteriaIds: (row.criteria_ids as string[]) || [],
    criteriaWeights: (row.criteria_weights as Record<string, number>) || {},
    filterRules: row.filter_rules as unknown[] | undefined,
    itemsReady: row.items_ready as number || 0,
    itemsNeedingWork: row.items_needing_work as number || 0,
    lockedAt: row.locked_at ? new Date(row.locked_at as string) : undefined,
    lockedBy: row.locked_by as string | undefined,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
  };
}

// Helper to convert CycleComparisonSet to DB row
function comparisonSetToDb(cs: CycleComparisonSet, workspaceId: string | null): Record<string, unknown> {
  return {
    cycle_id: cs.cycleId,
    item_ids: cs.itemIds,
    criteria_ids: cs.criteriaIds,
    criteria_weights: cs.criteriaWeights,
    filter_rules: cs.filterRules || [],
    items_ready: cs.itemsReady,
    items_needing_work: cs.itemsNeedingWork,
    locked_at: cs.lockedAt?.toISOString() || null,
    locked_by: cs.lockedBy || null,
    workspace_id: workspaceId,
  };
}

// Helper to sync participation to Supabase
async function syncParticipationToSupabase(p: CycleItemParticipation): Promise<void> {
  if (!isSupabaseAvailable()) return;
  
  const supabase = getSafeSupabaseClient();
  if (!supabase) return;
  
  const workspaceId = getCurrentWorkspaceId();
  const dbRecord = participationToDb(p, workspaceId);
  
  const { error } = await supabase
    .from(PARTICIPATION_TABLE)
    .upsert(dbRecord, { onConflict: 'cycle_id,item_id' });
  
  if (error) {
    console.error('[CycleParticipationStore] Failed to sync participation:', error);
  }
}

// Helper to sync comparison set to Supabase
async function syncComparisonSetToSupabase(cs: CycleComparisonSet): Promise<void> {
  if (!isSupabaseAvailable()) return;
  
  const supabase = getSafeSupabaseClient();
  if (!supabase) return;
  
  const workspaceId = getCurrentWorkspaceId();
  const dbRecord = comparisonSetToDb(cs, workspaceId);
  
  const { error } = await supabase
    .from(COMPARISON_SET_TABLE)
    .upsert(dbRecord, { onConflict: 'cycle_id' });
  
  if (error) {
    console.error('[CycleParticipationStore] Failed to sync comparison set:', error);
  }
}

export const useCycleParticipationStore = create<CycleParticipationState>()(
  (set, get) => ({
    ...initialState,
    
    // Supabase sync
    fetchFromSupabase: async () => {
      if (!isSupabaseAvailable()) {
        console.log('[CycleParticipationStore] Supabase not available');
        return;
      }
      
      set({ isLoading: true, error: null });
      
      try {
        const supabase = getSafeSupabaseClient();
        if (!supabase) return;
        
        // Fetch participations
        const { data: participationData, error: participationError } = await supabase
          .from(PARTICIPATION_TABLE)
          .select('*')
          .order('added_at', { ascending: false });
        
        if (participationError) {
          console.error('[CycleParticipationStore] Fetch participations error:', participationError);
          set({ isLoading: false, error: participationError.message });
          return;
        }
        
        // Fetch comparison sets
        const { data: comparisonData, error: comparisonError } = await supabase
          .from(COMPARISON_SET_TABLE)
          .select('*');
        
        if (comparisonError) {
          console.error('[CycleParticipationStore] Fetch comparison sets error:', comparisonError);
          set({ isLoading: false, error: comparisonError.message });
          return;
        }
        
        // Convert to store format
        const participationsMap: Record<string, CycleItemParticipation> = {};
        for (const row of participationData || []) {
          const p = dbToParticipation(row);
          participationsMap[participationKey(p.cycleId, p.itemId)] = p;
        }
        
        const comparisonSetsMap: Record<string, CycleComparisonSet> = {};
        for (const row of comparisonData || []) {
          const cs = dbToComparisonSet(row);
          comparisonSetsMap[cs.cycleId] = cs;
        }
        
        set({
          participations: participationsMap,
          comparisonSets: comparisonSetsMap,
          isLoading: false,
          error: null,
        });
        
        console.log(`[CycleParticipationStore] Loaded ${participationData?.length || 0} participations, ${comparisonData?.length || 0} comparison sets`);
      } catch (err) {
        console.error('[CycleParticipationStore] Fetch exception:', err);
        set({ isLoading: false, error: err instanceof Error ? err.message : 'Unknown error' });
      }
    },
      
      // Add item to cycle
      addItemToCycle: (input: AddItemToCycleInput): CycleItemParticipation => {
        const id = uuidv4();
        const now = new Date();
        const key = participationKey(input.cycleId, input.itemId);
        const actorId = getActorId();
        
        const participation: CycleItemParticipation = {
          id,
          cycleId: input.cycleId,
          itemId: input.itemId,
          inclusionReason: input.reason,
          inclusionNote: input.note,
          cycleStatus: 'nominated',
          cycleAttributeValues: [],
          addedAt: now,
          addedBy: actorId,
          participatedInSessionIds: [],
          cycleRankings: [],
        };
        
        set(state => ({
          participations: { ...state.participations, [key]: participation },
        }));
        
        // Sync to Supabase
        syncParticipationToSupabase(participation);

        // Emit audit event so cycle membership appears in Item History
        emitCycleAuditEvent(
          'item_added_to_cycle',
          input.itemId,
          input.cycleId,
          input.note ? `Added to cycle: ${input.note}` : 'Added to cycle',
          { inclusionReason: input.reason },
        );
        
        return participation;
      },
      
      // Batch add items
      batchAddItemsToCycle: (input: BatchAddItemsToCycleInput): CycleItemParticipation[] => {
        const now = new Date();
        const results: CycleItemParticipation[] = [];
        const updates: Record<string, CycleItemParticipation> = {};
        const actorId = getActorId();
        
        for (const itemId of input.itemIds) {
          const id = uuidv4();
          const key = participationKey(input.cycleId, itemId);
          
          const participation: CycleItemParticipation = {
            id,
            cycleId: input.cycleId,
            itemId,
            inclusionReason: input.reason,
            inclusionNote: input.note,
            cycleStatus: 'nominated',
            cycleAttributeValues: [],
            addedAt: now,
            addedBy: actorId,
            participatedInSessionIds: [],
            cycleRankings: [],
          };
          
          updates[key] = participation;
          results.push(participation);
        }
        
        set(state => ({
          participations: { ...state.participations, ...updates },
        }));
        
        // Sync all to Supabase and emit audit events
        for (const p of results) {
          syncParticipationToSupabase(p);
          emitCycleAuditEvent(
            'item_added_to_cycle',
            p.itemId,
            input.cycleId,
            input.note ? `Added to cycle: ${input.note}` : 'Added to cycle',
            { inclusionReason: input.reason },
          );
        }
        
        return results;
      },
      
      // Remove item from cycle
      removeItemFromCycle: (cycleId: string, itemId: string) => {
        const key = participationKey(cycleId, itemId);
        const existing = get().participations[key];
        if (!existing) return;
        const actorId = getActorId();
        
        const updated: CycleItemParticipation = {
          ...existing,
          cycleStatus: 'excluded',
          excludedAt: new Date(),
          excludedBy: actorId,
        };
        
        set(state => ({
          participations: { ...state.participations, [key]: updated },
        }));
        
        // Sync to Supabase
        syncParticipationToSupabase(updated);

        emitCycleAuditEvent('item_removed_from_cycle', itemId, cycleId, 'Removed from cycle');
      },
      
      // Update cycle status
      updateCycleStatus: (cycleId: string, itemId: string, status: CycleItemStatus) => {
        const key = participationKey(cycleId, itemId);
        const existing = get().participations[key];
        if (!existing) return;
        const actorId = getActorId();
        const prevStatus = existing.cycleStatus;
        
        const updated: CycleItemParticipation = {
          ...existing,
          cycleStatus: status,
          ...(status === 'confirmed' && { confirmedAt: new Date(), confirmedBy: actorId }),
          ...(status === 'excluded' && { excludedAt: new Date(), excludedBy: actorId }),
        };
        
        set(state => ({
          participations: { ...state.participations, [key]: updated },
        }));
        
        // Sync to Supabase
        syncParticipationToSupabase(updated);

        emitCycleAuditEvent(
          'cycle_status_changed',
          itemId,
          cycleId,
          `Cycle status: ${prevStatus} → ${status}`,
          { from: prevStatus, to: status },
        );
      },
      
      // Set cycle-specific attribute value
      setCycleAttributeValue: (input: SetCycleAttributeValueInput) => {
        const key = participationKey(input.cycleId, input.itemId);
        const existing = get().participations[key];
        if (!existing) return;
        
        // Update or add attribute value
        const existingIndex = existing.cycleAttributeValues.findIndex(
          av => av.attributeKey === input.attributeKey
        );
        
        const newValue: AttributeValue = {
          attributeKey: input.attributeKey,
          value: input.value,
        };
        
        const cycleAttributeValues = [...existing.cycleAttributeValues];
        if (existingIndex >= 0) {
          cycleAttributeValues[existingIndex] = newValue;
        } else {
          cycleAttributeValues.push(newValue);
        }
        
        const updated = { ...existing, cycleAttributeValues };
        
        set(state => ({
          participations: {
            ...state.participations,
            [key]: updated,
          },
        }));
        
        // Sync to Supabase
        syncParticipationToSupabase(updated);
      },
      
      // Clear cycle-specific attribute value
      clearCycleAttributeValue: (cycleId: string, itemId: string, attributeKey: string) => {
        const key = participationKey(cycleId, itemId);
        const existing = get().participations[key];
        if (!existing) return;
        
        const cycleAttributeValues = existing.cycleAttributeValues.filter(
          av => av.attributeKey !== attributeKey
        );
        
        const updated = { ...existing, cycleAttributeValues };
        
        set(state => ({
          participations: {
            ...state.participations,
            [key]: updated,
          },
        }));
        
        // Sync to Supabase
        syncParticipationToSupabase(updated);
      },
      
      // Add ranking
      addRanking: (cycleId: string, itemId: string, ranking: Omit<CycleItemRanking, 'rankedAt'>) => {
        const key = participationKey(cycleId, itemId);
        const existing = get().participations[key];
        if (!existing) return;
        
        const newRanking: CycleItemRanking = {
          ...ranking,
          rankedAt: new Date(),
        };
        
        // Replace or add
        const existingIndex = existing.cycleRankings.findIndex(
          r => r.sessionId === ranking.sessionId
        );
        
        const cycleRankings = [...existing.cycleRankings];
        if (existingIndex >= 0) {
          cycleRankings[existingIndex] = newRanking;
        } else {
          cycleRankings.push(newRanking);
        }
        
        const updated = {
          ...existing,
          cycleRankings,
          cycleStatus: existing.cycleStatus === 'shaped_for_cycle' ? 'ranked' as CycleItemStatus : existing.cycleStatus,
        };
        
        set(state => ({
          participations: {
            ...state.participations,
            [key]: updated,
          },
        }));
        
        // Sync to Supabase
        syncParticipationToSupabase(updated);
      },
      
      // Update item ranking (convenience: cycleId, itemId, sessionId, rank)
      updateItemRanking: (cycleId: string, itemId: string, sessionId: string, rank: number) => {
        get().addRanking(cycleId, itemId, {
          sessionId,
          sessionName: sessionId,
          rank,
          rankedBy: getActorId(),
        });
      },
      
      // Mark session participation
      markSessionParticipation: (cycleId: string, itemId: string, sessionId: string) => {
        const key = participationKey(cycleId, itemId);
        const existing = get().participations[key];
        if (!existing) return;
        
        if (existing.participatedInSessionIds.includes(sessionId)) return;
        
        const updated = {
          ...existing,
          participatedInSessionIds: [...existing.participatedInSessionIds, sessionId],
        };
        
        set(state => ({
          participations: {
            ...state.participations,
            [key]: updated,
          },
        }));
        
        // Sync to Supabase
        syncParticipationToSupabase(updated);
      },
      
      // Carryover items
      carryoverItems: (options: CycleCarryoverOptions): CycleCarryoverResult => {
        const fromCycleItems = get().getItemsInCycle(options.fromCycleId);
        const now = new Date();
        
        let carriedOver = 0;
        let excluded = 0;
        const updates: Record<string, CycleItemParticipation> = {};
        const errors: string[] = [];
        
        for (const participation of fromCycleItems) {
          // Skip excluded items
          if (participation.cycleStatus === 'excluded') {
            excluded++;
            continue;
          }
          
          // Skip if explicitly excluded
          if (options.excludeItemIds?.includes(participation.itemId)) {
            excluded++;
            continue;
          }
          
          // Check criteria (simplified - would need more complex logic)
          // Skip ranked items unless carryOverBelowLine is set
          const hasRanking = participation.cycleRankings.length > 0;
          if (hasRanking && !options.carryOverBelowLine) {
            excluded++;
            continue;
          }
          
          // Create new participation
          const id = uuidv4();
          const key = participationKey(options.toCycleId, participation.itemId);
          const actorId = getActorId();
          
          const newParticipation: CycleItemParticipation = {
            id,
            cycleId: options.toCycleId,
            itemId: participation.itemId,
            inclusionReason: 'carryover',
            inclusionNote: options.carryoverNote || `Carried over from cycle ${options.fromCycleId}`,
            cycleStatus: 'nominated',
            cycleAttributeValues: [...participation.cycleAttributeValues], // Copy values
            addedAt: now,
            addedBy: actorId,
            participatedInSessionIds: [],
            cycleRankings: [],
          };
          
          updates[key] = newParticipation;
          carriedOver++;
        }
        
        set(state => ({
          participations: { ...state.participations, ...updates },
        }));
        
        // Sync all carried over items to Supabase and emit audit events
        for (const key of Object.keys(updates)) {
          const p = updates[key];
          syncParticipationToSupabase(p);
          emitCycleAuditEvent(
            'item_added_to_cycle',
            p.itemId,
            options.toCycleId,
            options.carryoverNote
              ? `Carried over from previous cycle: ${options.carryoverNote}`
              : 'Carried over from previous cycle',
            { inclusionReason: 'carryover', fromCycleId: options.fromCycleId },
          );
        }
        
        return {
          success: errors.length === 0,
          itemsCarriedOver: carriedOver,
          itemsExcluded: excluded,
          errors,
        };
      },
      
      // Create comparison set
      createComparisonSet: (cycleId: string, itemIds: string[], criteriaIds: string[]): CycleComparisonSet => {
        const now = new Date();
        
        const comparisonSet: CycleComparisonSet = {
          cycleId,
          itemIds,
          criteriaIds,
          criteriaWeights: {},
          itemsReady: 0,
          itemsNeedingWork: itemIds.length,
          createdAt: now,
          updatedAt: now,
        };
        
        set(state => ({
          comparisonSets: { ...state.comparisonSets, [cycleId]: comparisonSet },
        }));
        
        // Sync to Supabase
        syncComparisonSetToSupabase(comparisonSet);
        
        return comparisonSet;
      },
      
      // Update comparison set
      updateComparisonSet: (cycleId: string, updates: Partial<CycleComparisonSet>) => {
        const existing = get().comparisonSets[cycleId];
        if (!existing) return;
        
        const updated = { ...existing, ...updates, updatedAt: new Date() };
        
        set(state => ({
          comparisonSets: {
            ...state.comparisonSets,
            [cycleId]: updated,
          },
        }));
        
        // Sync to Supabase
        syncComparisonSetToSupabase(updated);
      },
      
      // Lock comparison set
      lockComparisonSet: (cycleId: string, lockedBy: string) => {
        get().updateComparisonSet(cycleId, {
          lockedAt: new Date(),
          lockedBy,
        });
      },
      
      // Unlock comparison set
      unlockComparisonSet: (cycleId: string) => {
        get().updateComparisonSet(cycleId, {
          lockedAt: undefined,
          lockedBy: undefined,
        });
      },
      
      // Get participation
      getParticipation: (cycleId: string, itemId: string): CycleItemParticipation | undefined => {
        return get().participations[participationKey(cycleId, itemId)];
      },
      
      // Get item participation (convenience: itemId, cycleId order for test/caller contract)
      getItemParticipation: (itemId: string, cycleId: string): CycleItemParticipation | undefined => {
        return get().getParticipation(cycleId, itemId);
      },
      
      // Get items in cycle
      getItemsInCycle: (cycleId: string): CycleItemParticipation[] => {
        return Object.values(get().participations).filter(p => p.cycleId === cycleId);
      },
      
      // Get cycles for item
      getCyclesForItem: (itemId: string): CycleItemParticipation[] => {
        return Object.values(get().participations).filter(p => p.itemId === itemId);
      },
      
      // Get cycle item summary (total items in cycle, for integration/test contract)
      getCycleItemSummary: (cycleId: string): { totalItems: number } => {
        const items = get().getItemsInCycle(cycleId).filter(p => p.cycleStatus !== 'excluded');
        return { totalItems: items.length };
      },
      
      // Get item participation summary
      getItemParticipationSummary: (itemId: string): ItemParticipationSummary => {
        const participations = get().getCyclesForItem(itemId);
        
        const activeCycles = participations
          .filter(p => p.cycleStatus !== 'excluded')
          .map(p => ({
            cycleId: p.cycleId,
            cycleName: p.cycleId, // Would need cycle data
            status: p.cycleStatus,
            rank: p.cycleRankings[0]?.rank,
          }));
        
        const allRanks = participations
          .flatMap(p => p.cycleRankings)
          .map(r => r.rank);
        
        return {
          itemId,
          itemTitle: '', // Would need item data
          activeCycles,
          completedCycles: [],
          totalCycleParticipations: participations.length,
          averageRank: allRanks.length > 0 ? allRanks.reduce((a, b) => a + b, 0) / allRanks.length : undefined,
          bestRank: allRanks.length > 0 ? Math.min(...allRanks) : undefined,
        };
      },
      
      // Get cycle participation summary
      getCycleParticipationSummary: (cycleId: string): CycleParticipationSummary => {
        const participations = get().getItemsInCycle(cycleId);
        
        const byStatus: Record<CycleItemStatus, number> = {
          nominated: 0,
          confirmed: 0,
          shaped_for_cycle: 0,
          ranked: 0,
          excluded: 0,
        };
        
        const byInclusionReason: Record<InclusionReason, number> = {
          manual_selection: 0,
          criteria_match: 0,
          stakeholder_request: 0,
          carryover: 0,
        };
        
        for (const p of participations) {
          byStatus[p.cycleStatus]++;
          byInclusionReason[p.inclusionReason]++;
        }
        
        const activeItems = participations.filter(p => p.cycleStatus !== 'excluded');
        
        return {
          cycleId,
          byStatus,
          totalItems: activeItems.length,
          readyItems: byStatus.shaped_for_cycle + byStatus.ranked,
          needsWorkItems: byStatus.nominated + byStatus.confirmed,
          sessionParticipationRate: 0, // Would need session data
          byInclusionReason,
        };
      },
      
      // Get comparison set
      getComparisonSet: (cycleId: string): CycleComparisonSet | undefined => {
        return get().comparisonSets[cycleId];
      },
      
      // Get ready items
      getReadyItems: (cycleId: string, requiredAttributes: string[]): CycleItemParticipation[] => {
        return get().getItemsInCycle(cycleId).filter(p => {
          if (p.cycleStatus === 'excluded') return false;
          
          const hasAllRequired = requiredAttributes.every(attr =>
            p.cycleAttributeValues.some(av => av.attributeKey === attr && av.value != null)
          );
          
          return hasAllRequired;
        });
      },
      
      // Get items needing work
      getItemsNeedingWork: (cycleId: string, requiredAttributes: string[]): CycleItemParticipation[] => {
        return get().getItemsInCycle(cycleId).filter(p => {
          if (p.cycleStatus === 'excluded') return false;
          
          const hasAllRequired = requiredAttributes.every(attr =>
            p.cycleAttributeValues.some(av => av.attributeKey === attr && av.value != null)
          );
          
          return !hasAllRequired;
        });
      },
      
      // Reset
      reset: () => set(initialState),
      
      // Loading
    setLoading: (loading: boolean) => set({ isLoading: loading }),
    setError: (error: string | null) => set({ error }),
  })
);

// Selector hooks
export const useCycleParticipation = (cycleId: string, itemId: string) =>
  useCycleParticipationStore(state => state.getParticipation(cycleId, itemId));

export const useItemsInCycle = (cycleId: string) =>
  useCycleParticipationStore(
    useShallow((state) => Object.values(state.participations).filter((p) => p.cycleId === cycleId)),
  );

export const useCyclesForItem = (itemId: string) =>
  useCycleParticipationStore(
    useShallow((state) => Object.values(state.participations).filter((p) => p.itemId === itemId)),
  );

export const useCycleParticipationSummary = (cycleId: string) =>
  useCycleParticipationStore(state => state.getCycleParticipationSummary(cycleId));
